import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, FileDown, Leaf, Sparkles, HeartPulse, ArrowRight, Store, FlaskConical, Wrench } from "lucide-react";
import { api, waLink } from "../lib/api";
import { useConfig } from "../lib/config";
import { Seo } from "../components/site/Seo";
import { ProductCard } from "../components/site/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const CATEGORIES = ["Todos", "Alimentos", "Cuidado del agua", "Accesorios"];

const GALLERY = [
  { img: "https://images.unsplash.com/photo-1636045466232-539c7bd7817e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxwbGFudGVkJTIwYXF1YXJpdW18ZW58MHx8fHwxNzkxMjMxOTAzfDA&ixlib=rb-4.1.0&q=85", tag: "Acuarios plantados", title: "Línea Ultra-Fresh" },
  { img: "https://images.unsplash.com/photo-1716349842846-81f5bb963bed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxkaXNjdXMlMjBmaXNofGVufDB8fHx8MTc5MTIzMTkwM3ww&ixlib=rb-4.1.0&q=85", tag: "Cíclidos y discos", title: "Nutrición especializada" },
  { img: "https://images.unsplash.com/photo-1703319959677-b8dd4f14c80e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxhcXVhcml1bSUyMHNocmltcHxlbnwwfHx8fDE3OTEyMzE5MTF8MA&ixlib=rb-4.1.0&q=85", tag: "Nano y gambarios", title: "Línea AZOO 9&1" },
  { img: "https://images.unsplash.com/photo-1550479125-d6ec537f108e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxhcXVhcml1bSUyMHBldCUyMHNob3B8ZW58MHx8fHwxNzkxMjMxOTAzfDA&ixlib=rb-4.1.0&q=85", tag: "Tiendas y veterinarias", title: "Distribución Tugeko S.A." },
];

function TaiwanFlag() {
  return (
    <svg viewBox="0 0 24 16" className="w-4 h-3 rounded-[2px] overflow-hidden" aria-label="Bandera de Taiwán" role="img">
      <rect width="24" height="16" fill="#DE2110" />
      <rect width="12" height="8" fill="#000095" />
      <circle cx="6" cy="4" r="2.2" fill="#fff" />
      <circle cx="6" cy="4" r="1" fill="#000095" />
    </svg>
  );
}

export default function HomePage() {
  const config = useConfig();
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("Todos");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (filter === "Todos" ? products : products.filter((p) => p.category === filter)),
    [products, filter]
  );

  const wa = waLink(config.whatsapp_number || "", "Hola, quiero consultar sobre productos AZOO.");
  const waB2b = waLink(config.whatsapp_number || "", "Hola, tengo una tienda/veterinaria y me interesa vender AZOO.");

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "AZOO Costa Rica",
        description: "Productos para acuarios de la marca taiwanesa AZOO, distribuidos en Costa Rica por Tugeko S.A.",
        telephone: config.whatsapp_display,
        areaServed: "Costa Rica",
        brand: { "@type": "Brand", name: "AZOO" },
      },
      {
        "@type": "FAQPage",
        mainEntity: (config.faqs || []).map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main id="inicio" data-testid="home-page">
      <Seo
        title="AZOO Costa Rica — Alimentos y productos para acuarios"
        description="AZOO, marca taiwanesa de productos para acuarios distribuida en Costa Rica por Tugeko S.A. Alimentos Ultra-Fresh 100% naturales, cuidado del agua y accesorios. Lo natural siempre es mejor."
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center text-white overflow-hidden bg-zinc-950" data-testid="hero-section">
        <img
          src="https://images.unsplash.com/photo-1691387824643-227cc84127cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxwbGFudGVkJTIwYXF1YXJpdW18ZW58MHx8fHwxNzkxMjMxOTAzfDA&ixlib=rb-4.1.0&q=85"
          alt="Acuario plantado con peces sanos"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-zinc-950/60 to-zinc-950"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#e00010]/20 border border-[#e00010]/50 text-red-300 text-xs font-semibold uppercase tracking-widest animate-fade-up" data-testid="hero-origin-badge">
            <TaiwanFlag /> Producto de Taiwán
          </div>
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tight leading-[1.05] animate-fade-up stagger-1" data-testid="hero-title">
            Lo natural siempre es mejor.
          </h1>
          <p className="text-base sm:text-xl text-zinc-300 max-w-2xl mx-auto font-light leading-relaxed animate-fade-up stagger-2" data-testid="hero-subtitle">
            AZOO es una reconocida marca taiwanesa de productos para acuarios. Alimentos Ultra-Fresh sin aditivos químicos ni colorantes artificiales, para peces con máxima vitalidad en Costa Rica.
          </p>
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up stagger-3">
            <a href="#productos" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-brand-500 hover:bg-brand-400 text-zinc-950 font-black text-sm uppercase tracking-wider transition-colors shadow-lg hover:shadow-brand-500/25" data-testid="hero-products-button">
              Ver productos
            </a>
            <a href={wa} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-sm backdrop-blur border border-white/20 transition-colors" data-testid="hero-whatsapp-button">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
          {config.catalog_pdf_url && (
            <a href={config.catalog_pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs text-brand-300 hover:text-brand-200 underline underline-offset-4 animate-fade-up stagger-4" data-testid="hero-catalog-link">
              <FileDown className="w-3.5 h-3.5" /> Descargar catálogo PDF
            </a>
          )}

          <div className="pt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 text-left border-t border-white/15 max-w-4xl mx-auto">
            {[
              ["ORIGEN", "Marca taiwanesa"],
              ["FÓRMULA", "100% natural"],
              ["SIN", "Colorantes artificiales"],
              ["DISTRIBUYE", "Tugeko S.A. · CR"],
            ].map(([k, v]) => (
              <div key={k} className="p-3 bg-black/40 backdrop-blur rounded-xl border border-white/10">
                <span className="text-xs text-zinc-400 block font-mono">{k}</span>
                <span className="text-sm font-bold text-brand-300">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POR QUÉ AZOO */}
      <section id="por-que" className="py-20 bg-white border-b border-zinc-200" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-700">¿Por qué AZOO?</span>
            <h2 className="text-3xl sm:text-4xl font-black text-zinc-950 mt-2">Bienestar real para tu acuario</h2>
            <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
              Desde su fundación, AZOO destaca por su compromiso con la innovación, la calidad y el desarrollo de productos que mejoran la salud y el bienestar de los peces y otros organismos acuáticos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Leaf, title: "Sin aditivos químicos ni colorantes artificiales", text: "Ultra-Fresh es un alimento natural que enriquece el bienestar de los peces." },
              { icon: Sparkles, title: "Dieta 100% natural", text: "Los alimentos Ultra-Fresh utilizan fuentes naturales para replicar una dieta natural y real." },
              { icon: HeartPulse, title: "Nutrición optimizada", text: "Dieta balanceada en macronutrientes, vitaminas y minerales. Enriquece el color, crecimiento y salud de los peces." },
            ].map((b, i) => (
              <div key={b.title} className={`p-6 rounded-2xl bg-zinc-50 border border-zinc-200 hover:border-brand-500 transition-colors animate-fade-up stagger-${i + 1}`} data-testid={`benefit-card-${i}`}>
                <b.icon className="w-8 h-8 text-brand-600 mb-4" />
                <h3 className="font-bold text-zinc-950 text-base leading-snug">{b.title}</h3>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Store, cat: "Alimentos", text: "Líneas Ultra-Fresh y AZOO 9&1 para cada especie y etapa de vida." },
              { icon: FlaskConical, cat: "Cuidado del agua", text: "Acondicionadores y soluciones biológicas para un agua segura y cristalina." },
              { icon: Wrench, cat: "Accesorios", text: "Tests y accesorios esenciales para el mantenimiento de tu acuario." },
            ].map((c) => (
              <button
                key={c.cat}
                onClick={() => { setFilter(c.cat); document.querySelector("#productos")?.scrollIntoView({ behavior: "smooth" }); }}
                className="text-left p-6 rounded-2xl bg-zinc-950 text-white hover:bg-zinc-900 transition-colors group"
                data-testid={`category-card-${c.cat.toLowerCase().replace(/ /g, "-")}`}
              >
                <c.icon className="w-7 h-7 text-brand-400 mb-4" />
                <h3 className="font-bold text-lg">{c.cat}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{c.text}</p>
                <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-400 mt-4 group-hover:gap-2 transition-all">
                  Explorar <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GALERÍA */}
      <section className="py-20 bg-zinc-900 text-white border-b border-zinc-800" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-brand-400">El mundo acuático AZOO</span>
              <h2 className="text-3xl sm:text-4xl font-black mt-1">Ecosistemas impulsados por AZOO</h2>
            </div>
            <p className="text-xs text-zinc-400 max-w-md">
              Desde nano-acuarios plantados hasta baterías comerciales de tiendas: hay una solución AZOO para cada acuario.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {GALLERY.map((g, i) => (
              <div key={g.tag} className={`aspect-[4/5] rounded-2xl border border-zinc-800 relative overflow-hidden group animate-fade-up stagger-${i + 1}`}>
                <img src={g.img} alt={g.tag} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 space-y-1">
                  <span className="text-[10px] font-mono text-brand-400 uppercase">{g.tag}</span>
                  <h3 className="text-sm font-bold text-white">{g.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATÁLOGO */}
      <section id="productos" className="py-20 bg-zinc-50 border-b border-zinc-200" data-testid="catalog-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10 space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-700">Catálogo</span>
            <h2 className="text-3xl sm:text-4xl font-black text-zinc-950">Productos AZOO disponibles en Costa Rica</h2>
            <p className="text-sm text-zinc-600">
              Este catálogo es una vitrina sin precios. Haz clic en "Consultar" y te indicamos por WhatsApp dónde comprar cada producto.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="category-filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                  filter === cat ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 border border-zinc-200 hover:border-brand-500"
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(/ /g, "-")}`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="products-grid">
            {filtered.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-sm text-zinc-500 py-12" data-testid="products-empty">No hay productos en esta categoría todavía.</p>
          )}
        </div>
      </section>

      {/* B2B */}
      <section id="distribuidores" className="py-20 bg-zinc-100 border-b border-zinc-200" data-testid="b2b-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 sm:p-12 rounded-3xl bg-zinc-900 text-white flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-4 max-w-xl">
              <span className="text-xs font-mono uppercase tracking-widest text-brand-400">Canal mayorista Tugeko S.A.</span>
              <h2 className="text-3xl font-black">¿Tienes una tienda o veterinaria? Vende AZOO</h2>
              <p className="text-sm text-zinc-300 leading-relaxed">
                Únete a la red de distribuidores de Tugeko S.A. en Costa Rica: más de 1.000 productos para mascotas, servicio personalizado y atención rápida. Déjanos tus datos y un asesor de ventas de tu zona te contactará.
              </p>
            </div>
            <div className="shrink-0 flex flex-col gap-3 w-full sm:w-auto">
              <a href={config.distributor_form_url} target="_blank" rel="noopener noreferrer" className="px-8 py-4 rounded-xl bg-brand-500 hover:bg-brand-400 text-zinc-950 font-black text-xs uppercase tracking-wider text-center transition-colors" data-testid="b2b-form-button">
                Quiero ser distribuidor
              </a>
              <a href={waB2b} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs uppercase tracking-wider text-center transition-colors border border-zinc-700" data-testid="b2b-whatsapp-button">
                <MessageCircle className="w-4 h-4" /> Escribir a un asesor
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CONSEJOS / BLOG */}
      <section id="consejos" className="py-20 bg-white border-b border-zinc-200" data-testid="blog-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-700">Consejos</span>
              <h2 className="text-3xl font-black text-zinc-950 mt-1">Aprende a cuidar tu acuario</h2>
            </div>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-brand-700 hover:text-brand-600 transition-colors" data-testid="blog-view-all">
              Ver todos los consejos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} className="space-y-3 group" data-testid={`article-card-${a.slug}`}>
                <div className="aspect-video rounded-xl overflow-hidden bg-zinc-100">
                  {a.image && <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <h3 className="text-base font-bold text-zinc-950 group-hover:text-brand-700 transition-colors leading-snug">{a.title}</h3>
                <p className="text-xs text-zinc-600 leading-relaxed">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="preguntas" className="py-20 bg-zinc-50 border-b border-zinc-200" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="text-center space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-700">Preguntas frecuentes</span>
            <h2 className="text-2xl sm:text-3xl font-black text-zinc-950">Resolvemos tus dudas</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {(config.faqs || []).map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl bg-white border border-zinc-200 px-5">
                <AccordionTrigger className="text-left font-bold text-zinc-900 text-sm hover:no-underline" data-testid={`faq-question-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-600 leading-relaxed" data-testid={`faq-answer-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="text-center text-xs text-zinc-500">
            ¿Quieres vender AZOO?{" "}
            <a href={config.distributor_form_url} target="_blank" rel="noopener noreferrer" className="text-brand-700 font-bold underline underline-offset-2" data-testid="faq-distributor-link">
              Completa el formulario de distribuidores
            </a>
          </p>
        </div>
      </section>

      {/* CONTACT RAIL */}
      <section className="py-20 bg-zinc-950 text-white" data-testid="contact-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-400">Canal directo</span>
          <h2 className="text-3xl sm:text-4xl font-black">Conecta con AZOO Costa Rica</h2>
          <p className="text-sm text-zinc-400 max-w-xl mx-auto">
            ¿Buscas un producto específico o quieres incorporar la marca en tu tienda? Escríbenos directamente.
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-4">
            <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-brand-500 hover:bg-brand-400 text-zinc-950 font-black text-sm uppercase tracking-wider transition-colors shadow-lg" data-testid="contact-whatsapp-button">
              <MessageCircle className="w-4 h-4" /> Consultar por WhatsApp
            </a>
            <a href={config.distributor_form_url} target="_blank" rel="noopener noreferrer" className="px-8 py-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm transition-colors border border-zinc-700" data-testid="contact-form-button">
              Formulario de distribuidores
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
